import React, { useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { DeepAnalysisUpsellSummary } from '../../../../../shared/api/controllers/engagementAnalyticsReporting';
import { barChartDefaults, nivoChartTheme, sparkChartColors as UPSELL_ITEM_COLORS } from '../../../../../shared/constants/chartTheme';
import { chart_blue_2 } from '../../../../../shared/constants/colors';
import { ChartTooltip } from './ChartTooltip';
import { EmptyStateMessage } from './ApplicationDashboard.styles';

interface UpsellLocationSummary {
    applicationId: string;
    summary: DeepAnalysisUpsellSummary;
}

interface UpsellsOverviewChartProps {
    summaries: UpsellLocationSummary[];
    hiddenItemKeys: Set<string>;
    spanishSessionKeys?: Set<string>;
    appNameMap?: Record<string, string>;
    sessionLanguages?: Record<string, 'english' | 'spanish'>;
    showLanguageBreakdown?: boolean;
    onClick: () => void;
}

export { UPSELL_ITEM_COLORS };

const formatItemName = (tag: string) => tag.replace(/-/g, ' ').split(' ')
    .filter(word => word && word.toLocaleLowerCase() !== 'upsold')
    .map(word => `${word.charAt(0).toLocaleUpperCase()}${word.slice(1).toLocaleLowerCase()}`).join(' ');

export const buildTopUpsellItems = (summaries: UpsellLocationSummary[], hiddenItemKeys: Set<string>, spanishSessionKeys?: Set<string>, appNameMap: Record<string, string> = {}, sessionLanguages?: Record<string, 'english' | 'spanish'>, limit = 10) => {
    const counts = new Map<string, Map<string, number>>();
    const languageCounts = new Map<string, Map<string, { english: number; spanish: number }>>();
    const addCount = (tag: string, applicationId: string, count: number) => {
        if (!Number.isFinite(count) || count <= 0) return;
        const key = tag.toLocaleLowerCase();
        if (hiddenItemKeys.has(key)) return;
        const locations = counts.get(key) || new Map<string, number>();
        locations.set(applicationId, (locations.get(applicationId) || 0) + count);
        counts.set(key, locations);
    };
    summaries.forEach(({ applicationId, summary }) => {
        Object.entries(summary.withSessionId || {}).forEach(([tag, sessions]) => {
            const key = tag.toLocaleLowerCase();
            if (hiddenItemKeys.has(key)) return;
            sessions.forEach(session => {
                const sessionKey = `${applicationId}|${session.sessionId}`;
                if (session.applicationId !== applicationId || (spanishSessionKeys && !spanishSessionKeys.has(sessionKey))) return;
                if (spanishSessionKeys) addCount(key, applicationId, 1);
                const language = sessionLanguages?.[sessionKey];
                if (!language) return;
                const locations = languageCounts.get(key) || new Map<string, { english: number; spanish: number }>();
                const languages = locations.get(applicationId) || { english: 0, spanish: 0 };
                languages[language]++;
                locations.set(applicationId, languages);
                languageCounts.set(key, locations);
            });
        });
        if (!spanishSessionKeys) {
            (summary.tagsTotals || []).forEach(({ tag, count }) => addCount(tag, applicationId, count || 0));
        }
    });
    return Array.from(counts.entries()).map(([key, locations]) => {
        const apps = Array.from(locations.entries()).map(([applicationId, count]) => {
            const languages = languageCounts.get(key)?.get(applicationId);
            // Show language counts only when they account for the full location total.
            return { appName: appNameMap[applicationId] || applicationId, count,
                ...(languages && languages.english + languages.spanish === count ? languages : {}) };
        }).sort((a, b) => b.count - a.count || a.appName.localeCompare(b.appName));
        return { key, name: formatItemName(key), value: apps.reduce((total, app) => total + app.count, 0), apps };
    }).sort((a, b) => b.value - a.value || a.name.localeCompare(b.name) || a.key.localeCompare(b.key)).slice(0, limit);
};

const UpsellsOverviewChart: React.FC<UpsellsOverviewChartProps> = ({ summaries, hiddenItemKeys, spanishSessionKeys, appNameMap, sessionLanguages, showLanguageBreakdown = false, onClick }) => {
    const data = useMemo(() => buildTopUpsellItems(summaries, hiddenItemKeys, spanishSessionKeys, appNameMap, sessionLanguages), [summaries, hiddenItemKeys, spanishSessionKeys, appNameMap, sessionLanguages]);
    const chartData = useMemo(() => data.map(({ key, value }) => ({ item: key, value })), [data]);
    if (!data.length) return <EmptyStateMessage>No upsells were found for the selected date range and assistants.</EmptyStateMessage>;
    return (
        <ResponsiveBar
            data={chartData}
            keys={['value']}
            indexBy="item"
            layout="vertical"
            margin={{ top: 20, right: 20, bottom: 90, left: 40 }}
            padding={0.3}
            valueScale={{ type: 'linear' }}
            indexScale={{ type: 'band', round: true }}
            colors={({ index }) => UPSELL_ITEM_COLORS[index % UPSELL_ITEM_COLORS.length]}
            borderRadius={4}
            borderWidth={0}
            axisTop={null}
            axisRight={null}
            axisBottom={{
                tickSize: 0, tickPadding: 12, tickRotation: -45,
                renderTick: tick => {
                    const name = data.find(item => item.key === tick.value)?.name || String(tick.value);
                    return <g transform={`translate(${tick.x},${tick.y + 4})`}>
                        <text textAnchor="end" dominantBaseline="middle" transform="rotate(-45)"
                            style={{ fontSize: '10px', fill: chart_blue_2, fontWeight: 500 }}>
                            <title>{name}</title>{name.length > 18 ? `${name.slice(0, 17)}…` : name}
                        </text>
                    </g>;
                }
            }}
            axisLeft={{ tickSize: 0, tickPadding: 8, tickRotation: 0, format: value => Number.isInteger(Number(value)) ? value : '' }}
            enableGridX={false}
            enableGridY={barChartDefaults.enableGridY}
            enableLabel={false}
            theme={nivoChartTheme}
            animate={barChartDefaults.animate}
            motionConfig={barChartDefaults.motionConfig}
            tooltip={({ indexValue, value, color }) => {
                const item = data.find(item => item.key === indexValue);
                return <ChartTooltip title={item?.name || String(indexValue)} value={value} valueLabel="successful upsells" color={color}
                    apps={item?.apps || []} showLanguageBreakdown={showLanguageBreakdown} spanishOnly={!!spanishSessionKeys} />;
            }}
            onClick={onClick}
            role="application"
            ariaLabel="Top ten upsold items by quantity"
        />
    );
};

export default UpsellsOverviewChart;
